import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle, Mail } from "lucide-react";
import api from "../lib/api";
import Avatar from "../components/Avatar";

export default function Members() {
  const [members, setMembers] = useState([]);
  const [query, setQuery] = useState("");
  const [sectorFilter, setSectorFilter] = useState("All");

  useEffect(() => {
    api.get("/members").then((r) => setMembers(r.data));
  }, []);

  const sectors = useMemo(() => ["All", ...Array.from(new Set(members.map((m) => m.sector).filter(Boolean)))], [members]);

  const filtered = members.filter((m) => {
    if (sectorFilter !== "All" && m.sector !== sectorFilter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      m.name?.toLowerCase().includes(q) ||
      m.sector?.toLowerCase().includes(q) ||
      (m.sub_specialties || []).join(" ").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-8" data-testid="members-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <div className="overline">Directory</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F0EDE8] mt-2 tracking-tight">Members</h1>
          <p className="text-[#B8B3AC] mt-3 max-w-xl">
            The full table. Each member is the reference in their sector. Tap a card to see what they bring — and what they are looking for.
          </p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-initial sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9590]" />
            <input
              data-testid="members-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search members or sectors…"
              className="w-full bg-[#1A1A1A] border border-[#C9A84C]/20 rounded-md pl-9 pr-3 py-2.5 text-sm text-[#F0EDE8] placeholder-[#9A9590]/60 focus:outline-none focus:border-[#C9A84C]/60"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="sector-filter">
        {sectors.map((s) => (
          <button
            key={s}
            onClick={() => setSectorFilter(s)}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest transition ${
              sectorFilter === s
                ? "bg-[#C9A84C] text-[#0D0D0D]"
                : "border border-[#C9A84C]/25 text-[#9A9590] hover:border-[#C9A84C]/55 hover:text-[#C9A84C]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((m) => (
          <Link
            key={m.id}
            to={`/members/${m.id}`}
            className="ts-card p-7 flex flex-col gap-4 hover:-translate-y-0.5 transition-transform duration-300"
            data-testid={`member-card-${m.id}`}
          >
            <div className="flex items-start gap-4">
              <Avatar name={m.name} photoUrl={m.photo_url} color={m.avatar_color} size={56} />
              <div className="min-w-0 flex-1">
                <div className="font-serif text-xl text-[#F0EDE8]">{m.name}</div>
                <div className="text-xs uppercase tracking-widest text-[#C9A84C] mt-1">{m.sector}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#9A9590] mt-0.5">
                  {m.group_role}{m.status === "on_leave" ? " · On Leave" : ""}
                </div>
              </div>
            </div>
            {m.sub_specialties?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {m.sub_specialties.slice(0, 3).map((s) => (
                  <span key={s} className="text-[10px] uppercase tracking-widest text-[#9A9590] border border-[#C9A84C]/20 rounded-full px-2.5 py-1">
                    {s}
                  </span>
                ))}
              </div>
            )}
            {m.what_they_bring && (
              <p className="text-sm text-[#9A9590] line-clamp-3 leading-relaxed">{m.what_they_bring}</p>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-[#C9A84C]/10 text-xs text-[#9A9590]">
              <span>{m.active_deals_count} active deals</span>
              <div className="flex items-center gap-3">
                {m.whatsapp && <MessageCircle size={14} className="text-[#C9A84C]" />}
                {m.email && <Mail size={14} className="text-[#C9A84C]" />}
              </div>
            </div>
          </Link>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full ts-card p-12 text-center" data-testid="members-empty">
            <Search size={28} className="text-[#C9A84C]/60 mx-auto mb-4" strokeWidth={1.5}/>
            <div className="font-serif text-xl text-[#F0EDE8]">No members match your search.</div>
            <p className="text-sm text-[#B8B3AC] mt-2">Try a different name or sector.</p>
          </div>
        )}
      </div>
    </div>
  );
}
